import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Input, Avatar, Spin, Button, message as antdMessage, Modal } from 'antd';
import { UserOutlined, RobotOutlined, SendOutlined, CloseOutlined, PaperClipOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { v4 as uuidv4 } from 'uuid';
import { renderAsync } from 'docx-preview';
import * as XLSX from 'xlsx';
import { EMBED_CONFIG, getEmbedTheme, isTenant188, TENANT_188_THEME } from '../../configs/EmbedConfig';
import embedAuthService from '../../services/EmbedAuthService';
import { checkLimitError } from '../../configs/LimitsConfig';
import botLogo from '../../assets/svg/bot.png';
import userLogo from '../../assets/svg/user.png';

import {
  generateMessageId,
  formatTimestamp,
  postMessageToParent,
  listenToParentMessages,
  debounce,
  parseApiError,
  maximizeIframeForChat,
  showNewSessionConfirmation
} from '../../utils/embedUtils';
import { sendMessageToAgent } from './chatService';
import { getTenantIdFromToken } from 'utils/jwtUtils';
import { getSimpleWelcomeMessage } from '../../utils/welcomeMessageUtils';
import VideoCallButton from './VideoCallButton';
import { env } from '../../configs/EnvironmentConfig';

// Helper functions to reduce cognitive complexity (SonarQube fix - line 312)
const extractErrorMessage = (error) => {
  if (error?.response?.data?.detail?.message) {
    return error.response.data.detail.message;
  }
  if (error?.response?.data?.detail) {
    return JSON.stringify(error.response.data.detail);
  }
  if (error?.message) {
    return error.message;
  }
  return 'Sorry, something went wrong.';
};

const logErrorForDebugging = (error) => {
  if (process.env.NODE_ENV === 'development') {
    console.error('[ChatbotWidget] Error object:', error);
    if (error?.response) {
      console.error('[ChatbotWidget] error.response:', error.response);
      if (error.response.data) {
        console.error('[ChatbotWidget] error.response.data:', error.response.data);
      }
    }
  }
};

const createErrorMessage = (errorMessage) => ({
  id: generateMessageId(),
  role: 'system',
  content: errorMessage,
  timestamp: Date.now(),
  isError: true
});

/**
 * Helper function to render message content (SonarQube fix - extract nested ternary)
 */
const mapFilesForMessage = (files) =>
  files.map((f) => {
    const isImage = isImageFile(f);
    const isPdf = isPdfFile(f);
    const isWord = isWordFile(f);
    const isPreviewable =
      isImage || isPdf || isWord || isCsvFile(f) || isTextFile(f) || isExcelFile(f) || isPptFile(f);
    const openUrl = isPreviewable ? URL.createObjectURL(f) : null;
    return {
      name: f.name,
      type: f.type,
      size: f.size,
      previewUrl: isImage ? openUrl : null,
      openUrl,
    };
  });

const getFileOpenUrl = (file) =>
  file.openUrl || file.previewUrl || (file instanceof Blob ? URL.createObjectURL(file) : null);

const getImagePreviewUrls = (files) =>
  (files || [])
    .filter((f) => f.previewUrl || f.type?.startsWith('image/'))
    .map((f) => f.previewUrl || getFileOpenUrl(f))
    .filter(Boolean);

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  return `${Math.round(bytes / 1024)} kB`;
};

const isPdfFile = (file) =>
  file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');

const isWordFile = (file) => {
  const name = file.name?.toLowerCase() || '';
  return (
    file.type === 'application/msword' ||
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    name.endsWith('.doc') ||
    name.endsWith('.docx')
  );
};

const isDocxFile = (file) => (file.name?.toLowerCase() || '').endsWith('.docx');

const isImageFile = (file) => {
  const name = file.name?.toLowerCase() || '';
  return (
    file.type?.startsWith('image/') ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.png')
  );
};

const isCsvFile = (file) => {
  const name = file.name?.toLowerCase() || '';
  return file.type === 'text/csv' || name.endsWith('.csv');
};

const isTextFile = (file) => {
  const name = file.name?.toLowerCase() || '';
  return file.type === 'text/plain' || name.endsWith('.txt');
};

const isExcelFile = (file) => {
  const name = file.name?.toLowerCase() || '';
  return (
    file.type === 'application/vnd.ms-excel' ||
    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    name.endsWith('.xls') ||
    name.endsWith('.xlsx')
  );
};

const isPptFile = (file) => {
  const name = file.name?.toLowerCase() || '';
  return (
    file.type === 'application/vnd.ms-powerpoint' ||
    file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    name.endsWith('.ppt') ||
    name.endsWith('.pptx')
  );
};

const renderGenericFileAttachment = (file, onOpen, { badge, badgeColor, background, typeLabel }) => (
  <div
    role={onOpen ? 'button' : undefined}
    tabIndex={onOpen ? 0 : undefined}
    onClick={() => {
      if (onOpen) onOpen(file);
    }}
    onKeyDown={(e) => {
      if (!onOpen || (e.key !== 'Enter' && e.key !== ' ')) return;
      e.preventDefault();
      onOpen(file);
    }}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      background,
      borderRadius: 8,
      padding: '12px 14px',
      maxWidth: 280,
      cursor: onOpen ? 'pointer' : undefined,
    }}
  >
    <div
      style={{
        width: 36,
        height: 44,
        background: badgeColor,
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {badge}
    </div>
    <div>
      <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>{file.name}</div>
      <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
        {formatFileSize(file.size)} • {typeLabel}
      </div>
    </div>
  </div>
);

const renderSheetPreview = ({ loading, error, data, file }) => {
  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>Loading preview…</div>
    );
  }
  const downloadUrl = file ? getFileOpenUrl(file) : null;
  if (error || !data) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
        <p style={{ marginBottom: 12 }}>Couldn&apos;t preview this file.</p>
        {downloadUrl && (
          <a href={downloadUrl} download={file?.name}>Download instead</a>
        )}
      </div>
    );
  }
  return (
    <div style={{ padding: 12 }}>
      {data.sheets.map((sheet) => (
        <div key={sheet.name} style={{ marginBottom: 24 }}>
          {data.sheets.length > 1 && (
            <div style={{ fontWeight: 600, fontSize: 14, margin: '4px 0 8px' }}>{sheet.name}</div>
          )}
          <table
            style={{
              borderCollapse: 'collapse',
              width: '100%',
              fontSize: 13,
              tableLayout: 'auto',
            }}
          >
            <tbody>
              {sheet.rows.map((row, rowIndex) => (
                <tr key={`${sheet.name}-row-${rowIndex}`}>
                  {row.map((cell, colIndex) => (
                    <td
                      key={`${sheet.name}-cell-${rowIndex}-${colIndex}`}
                      style={{
                        border: '1px solid #e2e8f0',
                        padding: '4px 8px',
                        whiteSpace: 'nowrap',
                        background: rowIndex === 0 ? '#f8fafc' : '#fff',
                        fontWeight: rowIndex === 0 ? 600 : 400,
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {(sheet.truncatedRows || sheet.truncatedCols) && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
              Preview truncated.{' '}
              {downloadUrl && (
                <a href={downloadUrl} download={file?.name}>Download full file</a>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const renderPdfAttachment = (file, onOpenPdf) => (
  <div
    role={onOpenPdf ? 'button' : undefined}
    tabIndex={onOpenPdf ? 0 : undefined}
    onClick={() => {
      if (!onOpenPdf) return;
      const url = getFileOpenUrl(file);
      if (url) onOpenPdf(url);
    }}
    onKeyDown={(e) => {
      if (!onOpenPdf || (e.key !== 'Enter' && e.key !== ' ')) return;
      e.preventDefault();
      const url = getFileOpenUrl(file);
      if (url) onOpenPdf(url);
    }}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      background: '#e8f5e9',
      borderRadius: 8,
      padding: '12px 14px',
      maxWidth: 280,
      cursor: onOpenPdf ? 'pointer' : undefined,
    }}
  >
    <div
      style={{
        width: 36,
        height: 44,
        background: '#d32f2f',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      PDF
    </div>
    <div>
      <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>{file.name}</div>
      <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
        {formatFileSize(file.size)} • PDF
      </div>
    </div>
  </div>
);

const renderWordAttachment = (file, onOpenWord) => (
  <div
    role={onOpenWord ? 'button' : undefined}
    tabIndex={onOpenWord ? 0 : undefined}
    onClick={() => {
      if (onOpenWord) onOpenWord(file);
    }}
    onKeyDown={(e) => {
      if (!onOpenWord || (e.key !== 'Enter' && e.key !== ' ')) return;
      e.preventDefault();
      onOpenWord(file);
    }}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      background: '#e3f2fd',
      borderRadius: 8,
      padding: '12px 14px',
      maxWidth: 280,
      cursor: onOpenWord ? 'pointer' : undefined,
    }}
  >
    <div
      style={{
        width: 36,
        height: 44,
        background: '#1565c0',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      DOC
    </div>
    <div>
      <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>{file.name}</div>
      <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
        {formatFileSize(file.size)} • Word
      </div>
    </div>
  </div>
);

const renderAttachedFilePreview = (file, fileHandlers = {}, allFiles = [], compact = false) => {
  const { onOpenImage, onOpenPdf, onOpenWord, onOpenText, onOpenSheet, onOpenPpt } = fileHandlers;
  if (isPdfFile(file)) {
    return renderPdfAttachment(file, onOpenPdf);
  }
  if (isWordFile(file)) {
    return renderWordAttachment(file, onOpenWord);
  }
  if (isImageFile(file)) {
    const imageUrl = getFileOpenUrl(file);
    return (
      <img
        src={imageUrl}
        alt={file.name}
        onClick={() => imageUrl && onOpenImage?.(imageUrl, allFiles)}
        style={{
          ...(compact
            ? { width: 72, height: 72, objectFit: 'cover' }
            : { maxWidth: 120, maxHeight: 80 }),
          borderRadius: 8,
          display: 'block',
          cursor: onOpenImage ? 'pointer' : undefined,
        }}
      />
    );
  }
  if (isCsvFile(file)) {
    return renderGenericFileAttachment(file, onOpenText, {
      badge: 'CSV', badgeColor: '#2e7d32', background: '#e8f5e9', typeLabel: 'CSV',
    });
  }
  if (isTextFile(file)) {
    return renderGenericFileAttachment(file, onOpenText, {
      badge: 'TXT', badgeColor: '#546e7a', background: '#eceff1', typeLabel: 'Text',
    });
  }
  if (isExcelFile(file)) {
    return renderGenericFileAttachment(file, onOpenSheet, {
      badge: 'XLS', badgeColor: '#1b5e20', background: '#e8f5e9', typeLabel: 'Spreadsheet',
    });
  }
  if (isPptFile(file)) {
    return renderGenericFileAttachment(file, onOpenPpt, {
      badge: 'PPT', badgeColor: '#bf360c', background: '#fbe9e7', typeLabel: 'Presentation',
    });
  }
  return <span>📎 {file.name}</span>;
};

const renderMessageContent = (message, styles, fileHandlers = {}) => {
  const { onOpenImage, onOpenPdf, onOpenWord, onOpenText, onOpenSheet, onOpenPpt } = fileHandlers;
  if (message.isError) {
    return (
      <>
        <span style={styles.errorIcon}>⚠️</span>
        <span style={{ color: '#000000', fontWeight: 600 }}>{message.content}</span>
      </>
    );
  }
  
  if (message.role === 'agent') {
    return <ReactMarkdown>{message.content}</ReactMarkdown>;
  }

  if (message.files?.length) {
    return (
      <>
        {message.content && <span>{message.content}</span>}
        {message.files.map((file) => {
          let fileContent;
          if (file.previewUrl) {
            fileContent = (
              <img
                src={file.previewUrl}
                alt={file.name}
                onClick={() => onOpenImage?.(file.previewUrl, message.files)}
                style={{
                  maxWidth: 200,
                  borderRadius: 8,
                  display: 'block',
                  cursor: onOpenImage ? 'pointer' : undefined,
                }}
              />
            );
          } else if (isPdfFile(file)) {
            fileContent = renderPdfAttachment(file, onOpenPdf);
          } else if (isWordFile(file)) {
            fileContent = renderWordAttachment(file, onOpenWord);
          } else if (isImageFile(file)) {
            const imageUrl = getFileOpenUrl(file);
            fileContent = (
              <img
                src={imageUrl}
                alt={file.name}
                onClick={() => imageUrl && onOpenImage?.(imageUrl, message.files)}
                style={{
                  maxWidth: 200,
                  borderRadius: 8,
                  display: 'block',
                  cursor: onOpenImage ? 'pointer' : undefined,
                }}
              />
            );
          } else if (isCsvFile(file)) {
            fileContent = renderGenericFileAttachment(file, onOpenText, {
              badge: 'CSV', badgeColor: '#2e7d32', background: '#e8f5e9', typeLabel: 'CSV',
            });
          } else if (isTextFile(file)) {
            fileContent = renderGenericFileAttachment(file, onOpenText, {
              badge: 'TXT', badgeColor: '#546e7a', background: '#eceff1', typeLabel: 'Text',
            });
          } else if (isExcelFile(file)) {
            fileContent = renderGenericFileAttachment(file, onOpenSheet, {
              badge: 'XLS', badgeColor: '#1b5e20', background: '#e8f5e9', typeLabel: 'Spreadsheet',
            });
          } else if (isPptFile(file)) {
            fileContent = renderGenericFileAttachment(file, onOpenPpt, {
              badge: 'PPT', badgeColor: '#bf360c', background: '#fbe9e7', typeLabel: 'Presentation',
            });
          } else {
            fileContent = <span>📎 {file.name}</span>;
          }
          return (
            <div key={`${file.name}-${file.previewUrl || file.openUrl || 'file'}`} style={{ marginTop: message.content ? 8 : 0 }}>
              {fileContent}
            </div>
          );
        })}
      </>
    );
  }
  
  return <span>{message.content}</span>;
};

const ChatbotWidget = ({
  config = {},
  onClose,
  onMinimize,
  onMaximize,
  onError,
  onMessageSent,
  onMessageReceived,
  widgetError // <-- new prop
}) => {
  // State management
  const [chatHistory, setChatHistory] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [authInfo, setAuthInfo] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);

  const [limitInfo, setLimitInfo] = useState(null);
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [lastWidgetError, setLastWidgetError] = useState(null);
  const [extractedUserId, setExtractedUserId] = useState(null); // Store extracted user ID
  const [currentConfig, setCurrentConfig] = useState(config); // Dynamic config state
  const [chatLimitReached, setChatLimitReached] = useState(false); // Track if chat limit is reached
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [imagePreviewIndex, setImagePreviewIndex] = useState(0);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [wordPreviewFile, setWordPreviewFile] = useState(null);
  const [textPreviewFile, setTextPreviewFile] = useState(null);
  const [textPreviewContent, setTextPreviewContent] = useState('');
  const [sheetPreviewFile, setSheetPreviewFile] = useState(null);
  const [sheetPreviewData, setSheetPreviewData] = useState(null);
  const [sheetPreviewLoading, setSheetPreviewLoading] = useState(false);
  const [sheetPreviewError, setSheetPreviewError] = useState(false);
  const [pptPreviewFile, setPptPreviewFile] = useState(null);

  // Track whether this conversation is currently handled by a human agent (Flex).
  // Entered via is_connected_to_flex true / Flex WS; cleared when bot resumes after Flex
  // (normal completion, or explicit is_connected_to_flex: false).
  const [isHumanAgentActive, setIsHumanAgentActive] = useState(false);

  // WebSocket state
  const [websocketStatus, setWebsocketStatus] = useState('disconnected'); // 'disconnected', 'connecting', 'connected', 'error'
  const websocketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const connectWebSocketRef = useRef(null);
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000; // Start with 1 second

  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatContainerRef = useRef(null);
  const chatHistoryRef = useRef([]);
  const isHumanAgentActiveRef = useRef(false);

  // WebSocket connection function with reconnection logic
  // Backend route: /webchat/ws/{chat_agent_id}/{chat_id}/{tenant_id}
  const connectWebSocket = useCallback((chatId, backendUrl, tenantId, agentId) => {
    // Don't connect if already connected or if required params are missing
    if (!chatId || !backendUrl || !agentId) {
      if (process.env.NODE_ENV === 'development') {
        console.log('⚠️ WebSocket: Missing chatId, backendUrl or agentId, skipping connection');
      }
      return;
    }

    // Close existing connection if any
    if (websocketRef.current) {
      try {
        websocketRef.current.close();
      } catch (error) {
        console.error('Error closing existing WebSocket:', error);
      }
      websocketRef.current = null;
    }

    // Clear any pending reconnection attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Extract hostname from backend URL (handle both http/https and wss)
    let wsBaseUrl = backendUrl;
    try {
      const url = new URL(backendUrl.startsWith('http') ? backendUrl : `https://${backendUrl}`);
      // Convert http/https to ws/wss
      const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      wsBaseUrl = `${protocol}//${url.hostname}${url.port ? `:${url.port}` : ''}`;
    } catch (error) {
      // If URL parsing fails, assume it's already a hostname and use wss
      wsBaseUrl = `wss://${backendUrl}`;
    }

    const tenant = tenantId || 'default';
    const wsUrl = `${wsBaseUrl}/webchat/ws/${agentId}/${chatId}/${tenant}`;

    if (process.env.NODE_ENV === 'development') {
      console.log('🔌 WebSocket: Connecting to:', wsUrl);
    }

    setWebsocketStatus('connecting');
    reconnectAttemptsRef.current += 1;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ WebSocket: Connected successfully');
        }
        setWebsocketStatus('connected');
        reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (process.env.NODE_ENV === 'development') {
            console.log('📨 WebSocket: Received message:', data);
          }

          // Backend sends Flex replies as type "flex_reply". Also support legacy "agent_message".
          const isFlexReply = data.type === 'flex_reply' || data.type === 'agent_message';
          if (isFlexReply) {
            // Safeguard: never show suggested_reply as an agent message (avoid duplicates and wrong sender)
            const author = (data.author || 'Agent').trim();
            if (author === 'suggested_reply' || author.toLowerCase() === 'suggested_reply') {
              if (process.env.NODE_ENV === 'development') {
                console.log('📨 WebSocket: Ignoring suggested_reply message');
              }
              return;
            }

            const agentMessage = {
              id: generateMessageId(),
              role: 'agent',
              content: data.content || '',
              timestamp: data.timestamp ? new Date(data.timestamp).getTime() : Date.now(),
              author: author || 'Agent',
              source: 'flex_agent',
              isFlexAgent: true
            };

            setIsHumanAgentActive(true);
            isHumanAgentActiveRef.current = true;

            const incomingContent = String((data.content || '').trim());
            const prev = chatHistoryRef.current || [];
            const lastUserMsg = [...prev].reverse().find(m => m.role === 'user');
            const isUserEcho = lastUserMsg && incomingContent === String((lastUserMsg.content || '').trim());
            const isDuplicate = prev.some(msg =>
              msg.source === 'flex_agent' &&
              msg.content === agentMessage.content &&
              Math.abs(msg.timestamp - agentMessage.timestamp) < 1000
            );
            const shouldAdd = !isUserEcho && !isDuplicate;

            if (shouldAdd) {
              setChatHistory(prevState => [...prevState, agentMessage]);
              onMessageReceived?.(agentMessage);
              postMessageToParent('message-received', agentMessage);
            }
          }
        } catch (error) {
          console.error('❌ WebSocket: Error parsing message:', error);
          console.error('Raw message:', event.data);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket: Error occurred:', error);
        setWebsocketStatus('error');
      };

      ws.onclose = (event) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔌 WebSocket: Connection closed', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });
        }
        setWebsocketStatus('disconnected');
        websocketRef.current = null;

        // Attempt reconnection if not a clean close and haven't exceeded max attempts
        if (!event.wasClean && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current - 1); // Exponential backoff
          if (process.env.NODE_ENV === 'development') {
            console.log(`🔄 WebSocket: Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
          }

          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocketRef.current?.(chatId, backendUrl, tenantId, agentId);
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.warn('⚠️ WebSocket: Max reconnection attempts reached. Stopping reconnection.');
        }
      };

      websocketRef.current = ws;
    } catch (error) {
      console.error('❌ WebSocket: Failed to create connection:', error);
      setWebsocketStatus('error');
    }
  }, [onMessageReceived]);

  // Keep a ref to the latest callback to avoid TDZ/self-reference issues
  connectWebSocketRef.current = connectWebSocket;

  // Keep ref in sync for WebSocket handler (so we can skip user-echo without notifying)
  useEffect(() => {
    chatHistoryRef.current = chatHistory;
  }, [chatHistory]);

  // Disconnect WebSocket
  const disconnectWebSocket = useCallback(() => {
    if (websocketRef.current) {
      try {
        websocketRef.current.close(1000, 'Component unmounting');
      } catch (error) {
        console.error('Error closing WebSocket:', error);
      }
      websocketRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setWebsocketStatus('disconnected');
    reconnectAttemptsRef.current = 0;
  }, []);

  // Inject CSS animation for typing dots
  useEffect(() => {
    const styleId = 'chatbot-typing-animation';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes typingAnimation {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        .typing-dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          margin: 0 2px;
          background-color: currentColor;
          border-radius: 50%;
          animation: typingAnimation 1.4s infinite ease-in-out both;
        }

        .byd-input input::placeholder {
          color: #e7dde2ff !important;
        }

        input::placeholder {
          color: #908d8eff !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Configuration
  const theme = getEmbedTheme(currentConfig.theme);
  const showTimestamps = currentConfig.showTimestamps ?? EMBED_CONFIG.CHAT.SHOW_TIMESTAMPS;
  const welcomeMessage = currentConfig.welcomeMessage ?? EMBED_CONFIG.CHAT.WELCOME_MESSAGE;
  const placeholder = currentConfig.placeholder ?? EMBED_CONFIG.CHAT.INPUT_PLACEHOLDER;
  const allowMinimize = currentConfig.allowMinimize ?? EMBED_CONFIG.FEATURES.MINIMIZE_WIDGET;

  // Sync currentConfig with prop changes
  useEffect(() => {
    setCurrentConfig(config);
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Config updated:', { agentId: config.agentId, chatId: config.chatId });
    }
  }, [config]);

  // Initialize widget
  useEffect(() => {
    initializeWidget();
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (EMBED_CONFIG.CHAT.AUTO_SCROLL) {
      scrollToBottom();
    }
  }, [chatHistory]);

  // Listen for parent window messages
  useEffect(() => {
    const cleanup = listenToParentMessages(handleParentMessage);
    return cleanup;
  }, []);

  // Add widgetError as a system message if it changes and is not already in chatHistory
  useEffect(() => {
    if (
      widgetError &&
      widgetError !== lastWidgetError &&
      !chatHistory.some(
        msg => msg.isError && msg.content === widgetError
      )
    ) {
      setChatHistory(prev => [
        ...prev,
        {
          id: generateMessageId(),
          role: 'system',
          content: widgetError,
          timestamp: Date.now(),
          isError: true
        }
      ]);
      setLastWidgetError(widgetError);
    }
  }, [widgetError, lastWidgetError, chatHistory]);

  // Connect WebSocket when chat session starts (after initialization)
  useEffect(() => {
    if (isInitialized && currentConfig.chatId && currentConfig.agentId) {
      const backendUrl =
        currentConfig.baseUrl ||
        env?.IFRAME_WEBSOCKET_URL ||
        env?.IFRAME_CHAT_URL ||
        env?.API_WEBSOCKET ||
        env?.BASE_URL ||
        "https://chat-microservice.voiceracx.ai";

      // Resolve tenant_id dynamically: from JWT token first, then config, then default
      const tenantId = getTenantIdFromToken(currentConfig.token)
        ?? currentConfig.tenantId
        ?? currentConfig.tenant_id
        ?? 'default';

      if (process.env.NODE_ENV === 'development') {
        console.log('🔌 WebSocket: Initializing connection', {
          chatId: currentConfig.chatId,
          agentId: currentConfig.agentId,
          backendUrl,
          tenantId
        });
      }

      connectWebSocket(currentConfig.chatId, backendUrl, tenantId, currentConfig.agentId);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      disconnectWebSocket();
    };
  }, [isInitialized, currentConfig.chatId, currentConfig.agentId, connectWebSocket, disconnectWebSocket]);

  // Reconnect WebSocket when chatId changes (e.g., after maximize with new session)
  useEffect(() => {
    if (isInitialized && currentConfig.chatId && currentConfig.agentId) {
      const backendUrl =
        currentConfig.baseUrl ||
        env?.IFRAME_WEBSOCKET_URL ||
        env?.IFRAME_CHAT_URL ||
        env?.API_WEBSOCKET ||
        env?.BASE_URL ||
        "https://chat-microservice.voiceracx.ai";

      const tenantId = getTenantIdFromToken(currentConfig.token)
        ?? currentConfig.tenantId
        ?? currentConfig.tenant_id
        ?? 'default';
      // Small delay to ensure previous connection is cleaned up
      const timeoutId = setTimeout(() => {
        connectWebSocket(currentConfig.chatId, backendUrl, tenantId, currentConfig.agentId);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [currentConfig.chatId, isInitialized, connectWebSocket]);

  const initializeWidget = async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('=== WIDGET INITIALIZATION START ===');
        console.log('Widget config:', currentConfig);
      }

      setLoading(true);

      // Extract tenant ID from config (URL params) during initialization
      let userId = null;
      if (currentConfig.token) {
        userId = getTenantIdFromToken(currentConfig.token);
        if (process.env.NODE_ENV === 'development') {
          console.log('Extracted tenantId from token:', userId);
        }
      } else if (currentConfig.userId) {
        userId = currentConfig.userId;
        if (process.env.NODE_ENV === 'development') {
          console.log('Using userId from config:', userId);
        }
      } else {
        userId = 2; // fallback default
        if (process.env.NODE_ENV === 'development') {
          console.log('Using fallback userId:', userId);
        }
      }
      setExtractedUserId(userId);

      // Generate unique chat_id if not provided (never use '1' as fallback)
      const chatId = currentConfig.chatId || uuidv4();
      if (!currentConfig.chatId) {
        setCurrentConfig(prev => ({ ...prev, chatId }));
      }

      const authConfig = {
        method: currentConfig.authMethod,
        token: currentConfig.token,
        chat_agent_id: currentConfig.agentId,
        chat_id: chatId
      };

      const authResult = await embedAuthService.initialize(authConfig);

      if (!authResult.success) {
        throw new Error(authResult.error || 'Authentication failed');
      }

      setAuthInfo(authResult);

      // Set JWT token in localStorage for API calls
      // localStorage.setItem('auth_token', currentConfig.token);
      localStorage.setItem('sec_auth_m_tk', currentConfig.token);

      // Generate localized welcome message based on agent language from config
      // Use custom welcomeMessage from config if provided, otherwise use localized message
      const agentLanguage = currentConfig.language || 'English';
      const localizedWelcomeMessage = getSimpleWelcomeMessage(agentLanguage);
      const finalWelcomeMessage = currentConfig.welcomeMessage || localizedWelcomeMessage;

      // Add welcome message if configured
      if (finalWelcomeMessage) {
        const welcomeMsg = {
          id: generateMessageId(),
          role: 'agent',
          content: finalWelcomeMessage,
          timestamp: Date.now(),
          isWelcome: true
        };
        setChatHistory([welcomeMsg]);
      }

      setIsInitialized(true);
      if (process.env.NODE_ENV === 'development') {
        console.log('Widget initialized successfully');
      }
      postMessageToParent('widget-ready', {
        chat_agent_id: currentConfig.agentId,
        chat_id: chatId
      });
    } catch (error) {
      console.error('Widget initialization failed:', error);
      onError?.(error);
      const errorMessage = parseApiError(error, 'Failed to initialize chat.');
      const errorMsg = {
        id: generateMessageId(),
        role: 'system',
        content: errorMessage,
        timestamp: Date.now(),
        isError: true
      };
      setChatHistory([errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleParentMessage = useCallback((message) => {
    switch (message.type) {
      case 'vlm-chatbot-send-message':
        if (message.data.text) {
          handleSendMessage(message.data.text);
        }
        break;
      case 'vlm-chatbot-clear-chat':
        handleClearChat();
        break;
      case 'vlm-chatbot-minimize':
        handleMinimize();
        break;
      case 'vlm-chatbot-maximize':
        handleMaximize();
        break;
      default:
        break;
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Check usage limits and show warnings
  const checkUsageLimits = () => {
    if (!authInfo || !authInfo.subscriptionType) return { allowed: true };

    const currentMessageCount = chatHistory.filter(msg => msg.role === 'user').length;

    const limits = {
      'Free': { maxMessages: 10, maxDaily: 20 },
      'Starter': { maxMessages: 50, maxDaily: 100 },
      'Professional': { maxMessages: 200, maxDaily: 500 },
      'Enterprise': { maxMessages: -1, maxDaily: -1 }
    };

    const userLimits = limits[authInfo.subscriptionType] || limits['Free'];

    if (userLimits.maxMessages > 0 && currentMessageCount >= userLimits.maxMessages) {
      return {
        allowed: false,
        type: 'MESSAGE_LIMIT',
        message: authInfo.subscriptionType === 'Free'
          ? EMBED_CONFIG.CHAT.ERROR_MESSAGES.FREE_LIMIT_REACHED
          : EMBED_CONFIG.CHAT.ERROR_MESSAGES.MESSAGE_LIMIT_REACHED
      };
    }

    const warningThreshold = Math.floor(userLimits.maxMessages * (EMBED_CONFIG.CHAT.LIMIT_NOTIFICATIONS.SHOW_WARNING_AT_PERCENTAGE / 100));
    if (userLimits.maxMessages > 0 && currentMessageCount >= warningThreshold && !showLimitWarning) {
      setShowLimitWarning(true);
      setLimitInfo({
        type: 'WARNING',
        message: EMBED_CONFIG.CHAT.LIMIT_NOTIFICATIONS.WARNING_MESSAGE,
        remaining: userLimits.maxMessages - currentMessageCount
      });
    }

    return { allowed: true };
  };

  const handleSendMessage = async (messageText = inputValue) => {
    if ((!messageText.trim() && !attachedFiles.length) || loading || !isInitialized || !currentConfig.agentId || chatLimitReached) {
      if (chatLimitReached) {
        antdMessage.warning("Chat message limit reached. Please start a new chat session to continue.");
      }
      return;
    }

    // Check usage limits
    const limitCheck = checkUsageLimits();
    if (!limitCheck.allowed) {
      const errorMsg = {
        id: generateMessageId(),
        role: 'system',
        content: limitCheck.message,
        timestamp: Date.now(),
        isError: true,
        isLimitError: true
      };
      setChatHistory(prev => [...prev, errorMsg]);
      postMessageToParent('limit-reached', {
        type: limitCheck.type,
        message: limitCheck.message
      });
      return;
    }

    const rateLimitCheck = embedAuthService.checkRateLimit(authInfo?.sessionId);
    if (!rateLimitCheck.allowed) {
      antdMessage.warning(EMBED_CONFIG.CHAT.ERROR_MESSAGES.RATE_LIMIT);
      return;
    }

    if (messageText.length > EMBED_CONFIG.CHAT.MAX_MESSAGE_LENGTH) {
      antdMessage.warning(`Message too long. Maximum ${EMBED_CONFIG.CHAT.MAX_MESSAGE_LENGTH} characters.`);
      return;
    }

    const filesToSend = [...attachedFiles];

    const userMessage = {
      id: generateMessageId(),
      role: 'user',
      content: messageText.trim(),
      files: filesToSend.length ? mapFilesForMessage(filesToSend) : undefined,
      timestamp: Date.now()
    };

    setChatHistory(prev => [...prev, userMessage]);
    setInputValue('');
    setAttachedFiles([]);
    setLoading(true);

    onMessageSent?.(userMessage);
    postMessageToParent('message-sent', userMessage);

    try {
      if (!currentConfig.token) {
        throw new Error('JWT token is required to send messages. Please ensure you are authenticated.');
      }

      // Generate unique chat_id if not provided (never use '1' as fallback)
      const chatId = currentConfig.chatId || uuidv4();
      
      const apiPayload = {
        chat_agent_id: currentConfig.agentId,
        message: messageText.trim(),
        chat_id: chatId,
        user_id: extractedUserId,
        files: filesToSend,
        baseUrl: currentConfig.baseUrl  // Pass baseUrl to chatService
      };
      
      // Update config with generated chatId if it was missing
      if (!currentConfig.chatId) {
        setCurrentConfig(prev => ({ ...prev, chatId }));
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('🚀 Sending message with payload:', apiPayload);
      }
      const response = await sendMessageToAgent(apiPayload);

      // Flex session:
      // - Enter on is_connected_to_flex true (or Flex WS elsewhere).
      // - While in Flex, hide HTTP status/wait completions; show customer + Flex human only.
      // - After Flex ends, backend often returns a normal `completion` without the flex flag —
      //   treat that as bot resumed, exit human mode, and render the completion.
      const flexFlag = response?.is_connected_to_flex;
      const connectedToFlex = flexFlag === true;
      const flexExplicitlyEnded = flexFlag === false;

      const completion =
        response?.completion ??
        response?.message ??
        response?.answer ??
        null;
      const completionStr = completion != null ? String(completion).trim() : '';
      const completionLower = completionStr.toLowerCase();
      const isFlexStatusCompletion =
        completionLower.includes('sent to an agent') ||
        completionLower.includes('please wait for reply') ||
        completionLower.includes('wait for reply') ||
        completionLower.includes('connected to a human agent') ||
        completionLower.includes('they will assist you shortly');

      if (connectedToFlex) {
        setIsHumanAgentActive(true);
        isHumanAgentActiveRef.current = true;
      } else if (flexExplicitlyEnded) {
        setIsHumanAgentActive(false);
        isHumanAgentActiveRef.current = false;
      } else if (
        isHumanAgentActiveRef.current &&
        completionStr &&
        !isFlexStatusCompletion
      ) {
        // Bot resumed after Flex (e.g. { completion: "I'm here to help..." } with no flex flag)
        setIsHumanAgentActive(false);
        isHumanAgentActiveRef.current = false;
      }

      const isNowHuman = connectedToFlex || isHumanAgentActiveRef.current;

      if (!isNowHuman) {
        const sentText = messageText.trim();
        // Don't add agent message if backend echoed the user's message (show each message only once)
        if (completionStr && sentText && completionStr === sentText) {
          // Skip adding – user's message already shown on customer side
        } else {
          const botMessage = {
            id: generateMessageId(),
            role: 'agent',
            content: completion || 'No response received',
            timestamp: Date.now()
          };
          setChatHistory(prev => [...prev, botMessage]);
          onMessageReceived?.(botMessage);
          postMessageToParent('message-received', botMessage);
        }
      }
    } catch (error) {
      logErrorForDebugging(error);
      
      // Extract error code and message from API response
      const errorCode = error?.response?.data?.detail?.error_code || 
                       error?.data?.detail?.error_code || 
                       error?.error_code;
      const apiErrorMessage = error?.response?.data?.detail?.message || 
                             error?.data?.detail?.message || 
                             (typeof error?.response?.data?.detail === 'string' ? error?.response?.data?.detail : null) ||
                             (typeof error?.data?.detail === 'string' ? error?.data?.detail : null);
      
      // Check if this is a Pay-As-You-Go balance error (error code 040012 with balance-related message)
      const isBalanceError = errorCode === '040012' && 
                            apiErrorMessage && 
                            (apiErrorMessage.toLowerCase().includes('balance') || 
                             apiErrorMessage.toLowerCase().includes('exhausted') ||
                             apiErrorMessage.toLowerCase().includes('pay-as-you-go') ||
                             apiErrorMessage.toLowerCase().includes('pay as you go'));
      
      // If it's a balance error, show the actual backend message instead of treating it as a limit error
      if (isBalanceError) {
        const errorMessage = apiErrorMessage || extractErrorMessage(error);
        const errorMsg = createErrorMessage(errorMessage);
        setChatHistory(prev => [...prev, errorMsg]);
        antdMessage.error(apiErrorMessage || 'Your Pay-As-You-Go balance has been exhausted. Please top up your account.');
        onError?.(error);
        postMessageToParent('error', { error: errorMessage, type: 'balance-exhausted' });
        return; // Exit early for balance errors
      }
      
      // Check if this is a limit error
      const { isLimitError, limitType } = checkLimitError(error, false);
      
      if (isLimitError) {
        // Handle limit error - disable input and show appropriate message
        setChatLimitReached(true);
        const errorMessage = extractErrorMessage(error);
        const errorMsg = createErrorMessage(errorMessage);
        errorMsg.isLimitError = true;
        
        setChatHistory(prev => [...prev, errorMsg]);
        
        // Show user-friendly toast message
        antdMessage.warning("Chat message limit reached. Please start a new chat session to continue.");
        
        // Notify parent about limit reached
        postMessageToParent('limit-reached', {
          type: 'MESSAGE_LIMIT',
          message: errorMessage,
          limitType
        });
        
        onError?.(error);
        return; // Exit early to prevent further message attempts
      }
      
      // Regular error handling for non-limit errors
      const errorMessage = extractErrorMessage(error);
      const errorMsg = createErrorMessage(errorMessage);
      
      setChatHistory(prev => [...prev, errorMsg]);
      onError?.(error);
      postMessageToParent('error', { error: errorMessage, type: 'message-send-failed' });
    } finally {
      setLoading(false);
    }
  };

  const handleClearChat = () => {
    // Use localized welcome message when clearing chat
    const agentLanguage = currentConfig.language || 'English';
    const localizedWelcomeMessage = getSimpleWelcomeMessage(agentLanguage);
    const finalWelcomeMessage = currentConfig.welcomeMessage || localizedWelcomeMessage;
    
    setChatHistory(finalWelcomeMessage ? [{
      id: generateMessageId(),
      role: 'agent',
      content: finalWelcomeMessage,
      timestamp: Date.now(),
      isWelcome: true
    }] : []);
    // Reset limit state when clearing chat
    setChatLimitReached(false);
    postMessageToParent('chat-cleared');
  };

  const handleMinimize = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📌 Widget minimize requested - maintaining current session');
    }
    setIsMinimized(true);
    onMinimize?.();
    postMessageToParent('widget-minimized', {
      chat_agent_id: currentConfig.agentId,
      chat_id: currentConfig.chatId,
      sessionMaintained: true
    });
  };

  const handleMaximize = async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Widget maximize requested - checking for new session generation');
        console.log('🔍 Current config chatId:', currentConfig.chatId);
      }
      
      // Check if we should generate new UUID for maximized session
      const shouldAskConfirmation = currentConfig.conditions?.askConfirmation || false;
      
      if (shouldAskConfirmation) {
        // Show confirmation dialog
        const userConfirmed = await showNewSessionConfirmation('maximized chat session');
        
        if (!userConfirmed) {
          if (process.env.NODE_ENV === 'development') {
            console.log('❌ User cancelled maximization, keeping current session');
          }
          setIsMinimized(false);
          onMaximize?.();
          postMessageToParent('widget-maximized', { newSession: false });
          return;
        }
      }
      
      // Generate new chat ID for maximized session
      const newChatId = await maximizeIframeForChat(
        currentConfig.agentId, 
        { reason: 'maximize_for_chat' }, 
        false // Don't ask confirmation again since we already did
      );
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Generated new chat ID for maximized session:', newChatId);
      }
      
      // Update current config with new chat ID
      const updatedConfig = {
        ...currentConfig,
        chatId: newChatId,
        conditions: {
          ...currentConfig.conditions,
          maximizeForChat: true
        }
      };
      
      // CRITICAL: Update the component's config state
      setCurrentConfig(updatedConfig);
      if (process.env.NODE_ENV === 'development') {
        console.log('✨ Updated currentConfig with new chatId:', newChatId);
      }
      
      // Clear chat history to start fresh maximized session
      // Use localized welcome message
      const agentLanguage = currentConfig.language || 'English';
      const localizedWelcomeMessage = getSimpleWelcomeMessage(agentLanguage);
      const finalWelcomeMessage = currentConfig.welcomeMessage || localizedWelcomeMessage;
      
      setChatHistory(finalWelcomeMessage ? [{
        id: generateMessageId(),
        role: 'agent',
        content: finalWelcomeMessage,
        timestamp: Date.now(),
        isWelcome: true
      }] : []);
      
      // Reset limit state for new session
      setChatLimitReached(false);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Maximized session started with new UUID:', newChatId);
      }
      
      setIsMinimized(false);
      onMaximize?.();
      postMessageToParent('widget-maximized', { 
        newSession: true, 
        newChatId: newChatId,
        chat_agent_id: currentConfig.agentId,
        chat_id: newChatId
      });
      
    } catch (error) {
      console.error('Error during maximize:', error);
      // Fallback to simple maximize without new session
      setIsMinimized(false);
      onMaximize?.();
      postMessageToParent('widget-maximized', { newSession: false });
    }
  };

  const handleClose = () => {
    onClose?.();
    postMessageToParent('widget-closed');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const debouncedInputChange = debounce((value) => {
    postMessageToParent('typing', { isTyping: value.length > 0 });
  }, 300);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedInputChange(value);
  };

  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) {
      setAttachedFiles((prev) => [...prev, ...picked]);
    }
    e.target.value = '';
  };

  const removeAttachedFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const openImageGallery = (url, sourceFiles) => {
    const urls = getImagePreviewUrls(sourceFiles);
    const gallery = urls.length ? urls : (url ? [url] : []);
    setImagePreviewUrls(gallery);
    setImagePreviewIndex(Math.max(0, gallery.indexOf(url)));
  };

  const closeImageGallery = () => {
    setImagePreviewUrls([]);
    setImagePreviewIndex(0);
  };

  const fileHandlers = {
    onOpenImage: openImageGallery,
    onOpenPdf: (url) => setPdfPreviewUrl(url),
    onOpenWord: (file) => setWordPreviewFile(file),
    onOpenText: (file) => setTextPreviewFile(file),
    onOpenSheet: (file) => setSheetPreviewFile(file),
    onOpenPpt: (file) => setPptPreviewFile(file),
  };

  useEffect(() => {
    if (!textPreviewFile) {
      setTextPreviewContent('');
      return undefined;
    }
    let cancelled = false;
    const readText = async () => {
      try {
        const blob =
          textPreviewFile instanceof Blob
            ? textPreviewFile
            : await fetch(getFileOpenUrl(textPreviewFile)).then((res) => res.blob());
        const text = await blob.text();
        if (!cancelled) setTextPreviewContent(text);
      } catch {
        if (!cancelled) setTextPreviewContent('Unable to preview this file.');
      }
    };
    readText();
    return () => {
      cancelled = true;
    };
  }, [textPreviewFile]);

  useEffect(() => {
    if (!sheetPreviewFile) {
      setSheetPreviewData(null);
      setSheetPreviewError(false);
      setSheetPreviewLoading(false);
      return undefined;
    }
    let cancelled = false;
    const MAX_ROWS = 200;
    const MAX_COLS = 50;
    const parseSheet = async () => {
      setSheetPreviewLoading(true);
      setSheetPreviewError(false);
      try {
        const blob =
          sheetPreviewFile instanceof Blob
            ? sheetPreviewFile
            : await fetch(getFileOpenUrl(sheetPreviewFile)).then((res) => res.blob());
        const buffer = await blob.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheets = workbook.SheetNames.map((name) => {
          const rows = XLSX.utils.sheet_to_json(workbook.Sheets[name], {
            header: 1,
            blankrows: false,
            defval: '',
          });
          const truncatedRows = rows.length > MAX_ROWS;
          const limitedRows = rows.slice(0, MAX_ROWS).map((row) => {
            const arr = Array.isArray(row) ? row : [];
            return arr.slice(0, MAX_COLS).map((cell) => (cell == null ? '' : String(cell)));
          });
          return {
            name,
            rows: limitedRows,
            truncatedRows,
            truncatedCols: rows.some((row) => Array.isArray(row) && row.length > MAX_COLS),
          };
        });
        if (!cancelled) setSheetPreviewData({ sheets });
      } catch {
        if (!cancelled) setSheetPreviewError(true);
      } finally {
        if (!cancelled) setSheetPreviewLoading(false);
      }
    };
    parseSheet();
    return () => {
      cancelled = true;
    };
  }, [sheetPreviewFile]);

  const setWordContainer = useCallback((node) => {
    if (!node || !wordPreviewFile) return;
    node.innerHTML = '';
    if (!isDocxFile(wordPreviewFile)) {
      node.innerHTML =
        '<div style="padding:24px;text-align:center;color:#666;">Preview is not available for legacy .doc files. Please download to view.</div>';
      return;
    }
    const resolveBlob = async () => {
      if (wordPreviewFile instanceof Blob) return wordPreviewFile;
      const url = getFileOpenUrl(wordPreviewFile);
      if (!url) return null;
      const res = await fetch(url);
      return res.blob();
    };
    resolveBlob()
      .then((blob) => {
        if (blob) return renderAsync(blob, node);
      })
      .catch(() => {
        node.innerHTML =
          '<div style="padding:24px;text-align:center;color:#666;">Unable to preview this document.</div>';
      });
  }, [wordPreviewFile]);

  // Check if this is user_id 188 for BYD styling
  const isBYDUser = extractedUserId === 350;
  const resolvedTenantId =
    extractedUserId ?? currentConfig.tenantId ?? currentConfig.tenant_id;
  const isTenant188Theme = isTenant188(resolvedTenantId);

  useEffect(() => {
    if (isTenant188Theme) {
      const shellBg = TENANT_188_THEME.SHELL_BG;
      document.body.setAttribute('data-tenant188-theme', 'true');
      document.documentElement.setAttribute('data-tenant188-theme', 'true');
      document.body.style.backgroundColor = shellBg;
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.style.backgroundColor = shellBg;
        rootElement.style.margin = '0';
        rootElement.style.padding = '0';
        rootElement.style.minHeight = '100vh';
      }
      document.documentElement.style.backgroundColor = shellBg;
      document.documentElement.style.margin = '0';
      document.documentElement.style.padding = '0';
    }

    if (isBYDUser) {
      // Set data attribute for CSS targeting
      document.body.setAttribute('data-byd-theme', 'true');
      document.documentElement.setAttribute('data-byd-theme', 'true');
      
      // Set body background to match BYD dark theme
      document.body.style.backgroundColor = '#000000';
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      
      // Set root element background
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.style.backgroundColor = '#000000';
        rootElement.style.margin = '0';
        rootElement.style.padding = '0';
        rootElement.style.minHeight = '100vh';
      }
      
      // Set html element background
      document.documentElement.style.backgroundColor = '#000000';
      document.documentElement.style.margin = '0';
      document.documentElement.style.padding = '0';
    }
    
    return () => {
      if (isTenant188Theme) {
        document.body.removeAttribute('data-tenant188-theme');
        document.documentElement.removeAttribute('data-tenant188-theme');
        document.body.style.backgroundColor = '';
        document.body.style.margin = '';
        document.body.style.padding = '';
        const root188 = document.getElementById('root');
        if (root188) {
          root188.style.backgroundColor = '';
          root188.style.margin = '';
          root188.style.padding = '';
          root188.style.minHeight = '';
        }
        document.documentElement.style.backgroundColor = '';
        document.documentElement.style.margin = '';
        document.documentElement.style.padding = '';
      }
      // Cleanup on unmount
      if (isBYDUser) {
        document.body.removeAttribute('data-byd-theme');
        document.documentElement.removeAttribute('data-byd-theme');
        document.body.style.backgroundColor = '';
        document.body.style.margin = '';
        document.body.style.padding = '';
        const rootElement = document.getElementById('root');
        if (rootElement) {
          rootElement.style.backgroundColor = '';
          rootElement.style.margin = '';
          rootElement.style.padding = '';
          rootElement.style.minHeight = '';
        }
        document.documentElement.style.backgroundColor = '';
        document.documentElement.style.margin = '';
        document.documentElement.style.padding = '';
      }
    };
  }, [isBYDUser, isTenant188Theme]);

  // Set body and container backgrounds for BYD users to remove white space
  useEffect(() => {
    if (isBYDUser) {
      // Set data attribute for CSS targeting
      document.body.setAttribute('data-byd-theme', 'true');
      document.documentElement.setAttribute('data-byd-theme', 'true');
      
      // Set body background to match BYD dark theme
      document.body.style.backgroundColor = '#000000';
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      
      // Set root element background
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.style.backgroundColor = '#000000';
        rootElement.style.margin = '0';
        rootElement.style.padding = '0';
        rootElement.style.minHeight = '100vh';
      }
      
      // Set html element background
      document.documentElement.style.backgroundColor = '#000000';
      document.documentElement.style.margin = '0';
      document.documentElement.style.padding = '0';
    }
    
    return () => {
      // Cleanup on unmount
      if (isBYDUser) {
        document.body.removeAttribute('data-byd-theme');
        document.documentElement.removeAttribute('data-byd-theme');
        document.body.style.backgroundColor = '';
        document.body.style.margin = '';
        document.body.style.padding = '';
        const rootElement = document.getElementById('root');
        if (rootElement) {
          rootElement.style.backgroundColor = '';
          rootElement.style.margin = '';
          rootElement.style.padding = '';
          rootElement.style.minHeight = '';
        }
        document.documentElement.style.backgroundColor = '';
        document.documentElement.style.margin = '';
        document.documentElement.style.padding = '';
      }
    };
  }, [isBYDUser]);

  // Update config with extractedUserId so EmbedLayout can access it
  useEffect(() => {
    if (extractedUserId !== null) {
      setCurrentConfig(prev => ({ ...prev, extractedUserId }));
    }
  }, [extractedUserId]);

  // Remove the conditional header logic; always show the header

  if (!isInitialized && loading) {
    return (
      <div style={{ ...styles.loadingContainer, ...(isBYDUser ? { backgroundColor: '#000000' } : isTenant188Theme ? { backgroundColor: TENANT_188_THEME.SHELL_BG } : {}) }}>
        <Spin size="large" />
        <div style={{ ...styles.loadingText, ...(isBYDUser || isTenant188Theme ? { color: 'white' } : {}) }}>Initializing chat...</div>
      </div>
    );
  }

  if (isMinimized) {
    return (
      <div style={styles.minimizedContainer}>
        <button style={{ ...styles.minimizedButton, ...(isTenant188Theme ? styles.tenant188MinimizedButton : {}) }} onClick={handleMaximize}>
          <span style={styles.minimizedIcon}><RobotOutlined /></span> Chat
        </button>
      </div>
    );
  }

  return (
    <>
    <div style={{ ...styles.widget, ...getThemeStyles(theme), ...(isBYDUser ? styles.bydWidget : {}), ...(isTenant188Theme ? styles.tenant188Widget : {}) }}>
      {/* Header */}
      <div style={{ ...styles.header, ...(isBYDUser ? styles.bydHeader : {}), ...(isTenant188Theme ? styles.tenant188Header : {}) }}>
        <div style={isBYDUser ? styles.bydHeaderContent : isTenant188Theme ? styles.tenant188HeaderContent : styles.headerContent}>
          {!isBYDUser && (
            <>
              <img 
                src={botLogo}
                alt="Logo"
                style={{ ...styles.headerLogo, ...(isTenant188Theme ? styles.tenant188HeaderLogo : {}) }}
              />
              <span style={{ ...styles.headerTitle, ...(isTenant188Theme ? styles.tenant188HeaderTitle : {}) }}>AI Assistant</span>
            </>
          )}
          {isBYDUser && (
            <span style={styles.bydHeaderTitle}>BYD Virtual Assistant</span>
          )}
        </div>
      </div>

      {/* Limit Warning */}
      {limitInfo && limitInfo.type === 'WARNING' && (
        <div style={styles.limitWarning}>
          <div style={styles.limitWarningContent}>
            <span style={styles.limitWarningText}>{limitInfo.message}</span>
            {limitInfo.remaining && (
              <span style={styles.limitWarningRemaining}>
                {limitInfo.remaining} messages remaining
              </span>
            )}
          </div>
          <Button
            type="text"
            size="small"
            onClick={() => setLimitInfo(null)}
            style={styles.limitWarningClose}
          >
            ×
          </Button>
        </div>
      )}

      {/* Messages */}
      <div style={{ ...styles.messagesContainer, ...(isBYDUser ? styles.bydMessagesContainer : {}), ...(isTenant188Theme ? styles.tenant188MessagesContainer : {}) }} ref={chatContainerRef}>
        {chatHistory.map((message) => {
          const messageStyles = {
            ...styles.message,
            ...(message.role === 'user' 
              ? (isBYDUser ? styles.bydUserMessage : styles.userMessage)
              : (isBYDUser ? styles.bydAgentMessage : styles.agentMessage)
            ),
            ...(message.isError ? styles.errorMessage : {}),
            ...(message.isLimitError ? styles.limitErrorMessage : {})
          };
          
          const contentStyles = {
            ...styles.messageContent,
            ...(message.role === 'user' 
              ? (isBYDUser ? styles.bydUserMessageContent : isTenant188Theme ? styles.tenant188UserMessageContent : styles.userMessageContent)
              : (isBYDUser ? styles.bydAgentMessageContent : isTenant188Theme ? styles.tenant188AgentMessageContent : styles.agentMessageContent)
            ),
            ...(message.isError ? styles.errorMessageContent : {})
          };

          return (
            <div key={message.id} style={styles.messageWrapper}>
              <div style={messageStyles} className={`message ${message.role}-message ${message.isError ? 'error-message' : ''}`}>
                {message.role === 'agent'  && !isBYDUser && (
                  <img 
                    src={botLogo}
                    alt="Bot"
                    style={{
                      ...styles.messageAvatar,
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                )}
                {message.role === 'user' && !isBYDUser && (
                  <img 
                    src={userLogo}
                    alt="User"
                    style={{
                      ...styles.messageAvatar,
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                )}
                <div style={contentStyles} className={`message-content ${message.role}-message-content`}>
                  {message.isFlexAgent && (
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px', fontWeight: 500 }}>
                      {message.author || 'Human Agent'}
                    </div>
                  )}
                  {renderMessageContent(message, styles, fileHandlers)}
                {showTimestamps && (
                  <div style={styles.timestamp}>
                    {formatTimestamp(message.timestamp)}
                  </div>
                )}
              </div>
            </div>
          </div>
        );})}
        {/* Typing indicator */}
        {loading && !isMinimized && (
          <div style={styles.typingIndicatorWrapper}>
            <div style={styles.typingIndicator}>
              <span style={styles.typingDot}></span>
              <span style={styles.typingDot}></span>
              <span style={styles.typingDot}></span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{ ...styles.inputContainer, ...(isBYDUser ? styles.bydInputContainer : {}), ...(isTenant188Theme ? styles.tenant188InputContainer : {}) }}>
        {attachedFiles.length > 0 && (
          <div
            style={{
              padding: '0 0 8px',
              display: 'flex',
              flexDirection: 'row',
              gap: 8,
              overflowX: 'auto',
              flexWrap: 'nowrap',
            }}
          >
            {attachedFiles.map((file, index) => (
              <div key={`${file.name}-${index}`} style={{ position: 'relative', flexShrink: 0 }}>
                {renderAttachedFilePreview(file, fileHandlers, attachedFiles, true)}
                <Button
                  type="text"
                  size="small"
                  onClick={() => removeAttachedFile(index)}
                  aria-label="Remove file"
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    minWidth: 20,
                    height: 20,
                    padding: 0,
                    color: '#64748b',
                    background: 'rgba(255,255,255,0.9)',
                    borderRadius: '50%',
                  }}
                >
                  ×
                </Button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', position: 'relative', width: '100%', margin: 0, padding: 0 }}>
          {EMBED_CONFIG.FEATURES.FILE_UPLOAD && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                type="text"
                icon={<PaperClipOutlined />}
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || chatLimitReached}
                aria-label="Attach file"
              />
            </>
          )}
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onPressEnter={handleKeyPress}
            placeholder={isBYDUser ? "Type a message..." : (chatLimitReached ? "Message limit reached. Start a new chat session." : placeholder)}
            disabled={loading || chatLimitReached}
            className={isBYDUser ? 'byd-input' : isTenant188Theme ? 'tenant188-input' : ''}
            style={{ 
              ...styles.input, 
              flex: 1,
              minWidth: 0,
              ...(isBYDUser ? styles.bydInput : {}),
              ...(isTenant188Theme ? styles.tenant188Input : {})
            }}
          />
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined style={{ color: 'white' }} />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSendMessage();
            }}
            disabled={(!inputValue.trim() && !attachedFiles.length) || loading || !isInitialized || !currentConfig.agentId || chatLimitReached}
            style={{ ...styles.sendButton, ...(isBYDUser ? styles.bydSendButton : {}), ...(isTenant188Theme ? styles.tenant188SendButton : {}) }}
          />
          {isBYDUser && (
            <div style={styles.bydStarIcon}></div>
          )}
        </div>
      </div>

      {/* Powered by footer */}
      {EMBED_CONFIG.BRANDING.SHOW_POWERED_BY && !isBYDUser && (
        <div style={{ ...styles.poweredBy, ...(isTenant188Theme ? styles.tenant188PoweredBy : {}) }}>
          <a
            href={EMBED_CONFIG.BRANDING.POWERED_BY_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...styles.poweredByLink, ...(isTenant188Theme ? styles.tenant188PoweredByLink : {}) }}
          >
            {EMBED_CONFIG.BRANDING.POWERED_BY_TEXT}
          </a>
        </div>
      )}
    </div>
    <Modal
      open={imagePreviewUrls.length > 0}
      footer={null}
      onCancel={closeImageGallery}
      width="auto"
      centered
    >
      <img
        src={imagePreviewUrls[imagePreviewIndex]}
        alt="Attachment preview"
        style={{ maxWidth: '100%', maxHeight: '80vh', display: 'block', margin: '0 auto' }}
      />
      {imagePreviewUrls.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 12 }}>
          <Button
            type="text"
            icon={<LeftOutlined />}
            disabled={imagePreviewIndex === 0}
            onClick={() => setImagePreviewIndex((index) => index - 1)}
            aria-label="Previous image"
          />
          <span style={{ fontSize: 13, color: '#64748b' }}>{imagePreviewIndex + 1} / {imagePreviewUrls.length}</span>
          <Button
            type="text"
            icon={<RightOutlined />}
            disabled={imagePreviewIndex === imagePreviewUrls.length - 1}
            onClick={() => setImagePreviewIndex((index) => index + 1)}
            aria-label="Next image"
          />
        </div>
      )}
    </Modal>
    <Modal
      open={!!pdfPreviewUrl}
      footer={null}
      onCancel={() => setPdfPreviewUrl(null)}
      width="80%"
      centered
      title="PDF Preview"
      destroyOnClose
    >
      <iframe
        src={pdfPreviewUrl}
        title="PDF preview"
        style={{ width: '100%', height: '70vh', border: 'none' }}
      />
    </Modal>
    <Modal
      open={!!wordPreviewFile}
      footer={null}
      onCancel={() => setWordPreviewFile(null)}
      width="80%"
      centered
      title="Document Preview"
      destroyOnClose
    >
      <div
        ref={setWordContainer}
        style={{ width: '100%', height: '70vh', overflow: 'auto', background: '#fff' }}
      />
    </Modal>
    <Modal
      open={!!textPreviewFile}
      footer={null}
      onCancel={() => setTextPreviewFile(null)}
      width="80%"
      centered
      title={textPreviewFile?.name || 'File Preview'}
      destroyOnClose
    >
      <pre
        style={{
          width: '100%',
          height: '70vh',
          overflow: 'auto',
          background: '#fff',
          margin: 0,
          padding: 12,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'monospace',
          fontSize: 13,
        }}
      >
        {textPreviewContent}
      </pre>
    </Modal>
    <Modal
      open={!!sheetPreviewFile}
      footer={null}
      onCancel={() => setSheetPreviewFile(null)}
      width="80%"
      centered
      title={sheetPreviewFile?.name || 'Spreadsheet'}
      destroyOnClose
    >
      <div style={{ width: '100%', height: '70vh', overflow: 'auto', background: '#fff' }}>
        {renderSheetPreview({
          loading: sheetPreviewLoading,
          error: sheetPreviewError,
          data: sheetPreviewData,
          file: sheetPreviewFile,
        })}
      </div>
    </Modal>
    <Modal
      open={!!pptPreviewFile}
      footer={null}
      onCancel={() => setPptPreviewFile(null)}
      centered
      title="Presentation"
      destroyOnClose
    >
      <div style={{ padding: 16 }}>
        <p style={{ fontWeight: 600, marginBottom: 8 }}>{pptPreviewFile?.name}</p>
        <p style={{ color: '#666', marginBottom: 16 }}>
          {formatFileSize(pptPreviewFile?.size)} • Presentation
        </p>
        {pptPreviewFile && getFileOpenUrl(pptPreviewFile) && (
          <a href={getFileOpenUrl(pptPreviewFile)} download={pptPreviewFile.name}>
            Download file
          </a>
        )}
      </div>
    </Modal>
    </>
  );
};

// Styles
const styles = {
  widget: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'var(--embed-background-color, #ffffff)',
    borderRadius: 'var(--embed-border-radius, 12px)',
    border: '1px solid var(--embed-border-color, #e0e0e0)',
    boxShadow: 'var(--embed-shadow, 0 4px 24px rgba(0, 0, 0, 0.15))',
    fontFamily: 'var(--embed-font-family, system-ui, -apple-system, sans-serif)',
    overflow: 'hidden'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '16px'
  },
  loadingText: {
    color: 'var(--embed-text-color, #666)',
    fontSize: '14px'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: 'none',
    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
    color: 'white',
    boxShadow: '0 4px 20px rgba(24, 144, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    flexShrink: 0
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  headerLogo: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  headerAvatar: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)'
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: '600'
  },
  headerActions: {
    display: 'flex',
    gap: '4px'
  },
  headerButton: {
    color: 'white',
    border: 'none'
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    justifyContent: 'flex-start',
    minHeight: 0
  },
  messageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideInUp 0.3s ease-out',
    transformOrigin: 'bottom'
  },
  message: {
    display: 'flex',
    gap: '8px',
    maxWidth: '85%'
  },
  userMessage: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse'
  },
  userMessageContent: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#ffffff',
    borderRadius: '18px 18px 6px 18px',
    boxShadow: '0 2px 12px rgba(102, 126, 234, 0.3)',
    border: 'none'
  },
  agentMessage: {
    alignSelf: 'flex-start'
  },
  agentMessageContent: {
    background: '#f8f9fa',
    borderRadius: '18px 18px 18px 6px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e9ecef'
  },
  errorMessage: {
    alignSelf: 'center',
    maxWidth: '90%',
    margin: '8px 0',
  },
  errorMessageContent: {
    backgroundColor: '#ffcccc', // light red background
    color: '#000000', // black text
    borderRadius: '12px',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    border: '1px solid #ff6666' // darker border for contrast
  },
  errorIcon: {
    marginRight: 8,
    fontSize: 18,
    flexShrink: 0,
    color: '#ff0000', // bright red for icon
    textShadow: '0 0 1px rgba(0,0,0,0.2)'
  },
  limitErrorMessage: {
    alignSelf: 'center'
  },
  limitWarning: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    backgroundColor: '#fff3cd',
    borderLeft: '4px solid #ffc107',
    margin: '0 16px 8px 16px',
    borderRadius: '4px',
    fontSize: '12px'
  },
  limitWarningContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  limitWarningText: {
    color: '#856404',
    fontWeight: '500'
  },
  limitWarningRemaining: {
    color: '#856404',
    fontSize: '11px',
    opacity: 0.8
  },
  limitWarningClose: {
    color: '#856404',
    padding: '0',
    minWidth: '20px',
    height: '20px',
    fontSize: '16px',
    lineHeight: '1'
  },
  messageAvatar: {
    flexShrink: 0,
    marginTop: '2px'
  },
  messageContent: {
    padding: '10px 14px',
    borderRadius: '18px',
    backgroundColor: 'var(--embed-border-color, #f0f0f0)',
    fontSize: '14px',
    lineHeight: '1.4',
    wordBreak: 'break-word',
    position: 'relative',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    border: '1px solid var(--embed-border-color, #e9ecef)',
    transition: 'all 0.2s ease'
  },
  timestamp: {
    fontSize: '11px',
    color: 'var(--embed-secondary-color, #999)',
    marginTop: '4px'
  },
  inputContainer: {
    padding: '12px 16px',
    borderTop: '1px solid var(--embed-border-color, #e0e0e0)',
    flexShrink: 0,
    margin: 0,
    width: '100%',
    boxSizing: 'border-box'
  },
  input: {
    borderRadius: '10px',
    border: '2px solid #e9ecef',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    boxShadow: 'none',
    margin: 0,
    padding: '10px 16px',
    paddingTop: '10px',
    paddingBottom: '10px',
    height: '40px',
    minHeight: '40px',
    maxHeight: '40px',
    lineHeight: '20px',
    width: '100%',
    boxSizing: 'border-box',
    verticalAlign: 'middle',
    caretColor: 'var(--embed-primary-color, #1890ff)',
    fontFamily: 'var(--embed-font-family, system-ui, -apple-system, sans-serif)',
    backgroundColor: '#ffffff',
    color: '#333',
    outline: 'none',
    display: 'block',
    position: 'relative',
    transform: 'none',
    top: 'auto',
    left: 'auto'
  },
  sendButton: {
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
    border: 'none',
    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
    transition: 'all 0.2s ease',
    color: 'white'
  },
  poweredBy: {
    padding: '8px 16px',
    textAlign: 'center',
    borderTop: '1px solid var(--embed-border-color, #e0e0e0)',
    backgroundColor: 'var(--embed-border-color, #f8f8f8)'
  },
  poweredByLink: {
    fontSize: '11px',
    color: 'var(--embed-secondary-color, #999)',
    textDecoration: 'none'
  },
  minimizedContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'transparent',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    padding: '24px',
    boxSizing: 'border-box',
    pointerEvents: 'none'
  },
  minimizedButton: {
    background: '#1890ff',
    color: '#fff',
    border: 'none',
    borderRadius: 24,
    padding: '12px 24px',
    fontSize: 16,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
    pointerEvents: 'auto',
    zIndex: 10000
  },
  minimizedIcon: {
    marginRight: 8
  },
  typingIndicatorWrapper: {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    marginLeft: 8,
    marginBottom: 8,
  },
  typingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  typingDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#bbb',
    margin: '0 2px',
    animation: 'typing-bounce 1.2s infinite ease-in-out',
    animationDelay: '0s',
  },
  // BYD-specific styles for user_id 350
  bydWidget: {
    backgroundColor: '#2a2a2a',
    borderRadius: '12px',
    border: 'none',
    boxShadow: 'none'
  },
  bydHeader: {
    background: '#4a4a4a',
    borderBottom: 'none',
    boxShadow: 'none',
    padding: '16px 20px'
  },
  bydHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    width: '100%'
  },
  bydHeaderTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'white',
    textAlign: 'center'
  },
  bydCloseButton: {
    color: 'white',
    border: 'none',
    padding: '0',
    minWidth: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent'
  },
  bydMessagesContainer: {
    backgroundColor: '#2a2a2a',
    padding: '16px'
  },
  bydAgentMessage: {
    alignSelf: 'flex-start'
  },
  bydAgentMessageContent: {
    background: '#3a3a3a',
    color: 'white',
    borderRadius: '18px 18px 18px 6px',
    boxShadow: 'none',
    border: 'none'
  },
  bydUserMessage: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse'
  },
  bydUserMessageContent: {
    background: '#d32f2f', // Red color
    color: '#ffffff',
    borderRadius: '18px 18px 6px 18px',
    boxShadow: 'none',
    border: 'none'
  },
  bydInputContainer: {
    backgroundColor: '#2a2a2a',
    borderTop: 'none',
    padding: '16px'
  },
  bydInput: {
    backgroundColor: '#3a3a3a',
    border: 'none',
    color: 'white',
    borderRadius: '10px',
    padding: '10px 16px',
    height: '40px',
    lineHeight: '20px',
    caretColor: '#ffffff'
  },
  bydSendButton: {
    background: '#d32f2f', // Red color
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    border: 'none',
    boxShadow: 'none'
  },
  bydStarIcon: {
    position: 'absolute',
    bottom: '8px',
    right: '60px',
    fontSize: '16px',
    color: 'white',
    zIndex: 1,
    pointerEvents: 'none'
  },


  // Tenant 188 theme (from New_Architecture)
  tenant188Widget: {
    backgroundColor: TENANT_188_THEME.SHELL_BG,
    border: `1px solid ${TENANT_188_THEME.BORDER}`,
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
  },
  tenant188Header: {
    background: TENANT_188_THEME.HEADER_BACKGROUND,
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    position: 'relative',
    zIndex: 1,
  },
  tenant188HeaderContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  tenant188HeaderLogo: {
    backgroundColor: 'rgba(110, 184, 199, 0.12)',
    border: `1px solid ${TENANT_188_THEME.BORDER}`,
  },
  tenant188HeaderTitle: {
    color: TENANT_188_THEME.TEXT_ON_SHELL,
    fontWeight: '600',
  },
  tenant188MessagesContainer: {
    backgroundColor: TENANT_188_THEME.CHAT_BG,
  },
  tenant188AgentMessageContent: {
    background: TENANT_188_THEME.AGENT_BUBBLE,
    color: '#334155',
    borderRadius: '18px 18px 18px 6px',
    boxShadow: 'none',
    border: `1px solid ${TENANT_188_THEME.AGENT_BUBBLE_BORDER}`,
  },
  tenant188UserMessageContent: {
    background: TENANT_188_THEME.ACCENT_DARK,
    color: TENANT_188_THEME.TEXT_ON_SHELL,
    borderRadius: '18px 18px 6px 18px',
    boxShadow: '0 1px 4px rgba(15, 23, 42, 0.08)',
    border: 'none',
  },
  tenant188InputContainer: {
    backgroundColor: TENANT_188_THEME.SHELL_BG,
    borderTop: `1px solid ${TENANT_188_THEME.BORDER}`,
  },
  tenant188Input: {
    backgroundColor: '#ffffff',
    border: `1px solid ${TENANT_188_THEME.AGENT_BUBBLE_BORDER}`,
    caretColor: TENANT_188_THEME.ACCENT_DARK,
  },
  tenant188SendButton: {
    background: TENANT_188_THEME.ACCENT_DARK,
    boxShadow: '0 1px 6px rgba(15, 23, 42, 0.15)',
    color: TENANT_188_THEME.TEXT_ON_SHELL,
  },
  tenant188PoweredBy: {
    backgroundColor: TENANT_188_THEME.SHELL_BG,
    borderTop: `1px solid ${TENANT_188_THEME.BORDER}`,
  },
  tenant188PoweredByLink: {
    color: 'rgba(255, 255, 255, 0.5)',
  },
  tenant188MinimizedButton: {
    background: TENANT_188_THEME.ACCENT_DARK,
    color: TENANT_188_THEME.TEXT_ON_SHELL,
    fontWeight: '600',
  },
};

const getThemeStyles = (theme) => ({
  '--embed-primary-color': theme.PRIMARY_COLOR,
  '--embed-secondary-color': theme.SECONDARY_COLOR,
  '--embed-background-color': theme.BACKGROUND_COLOR,
  '--embed-text-color': theme.TEXT_COLOR,
  '--embed-border-color': theme.BORDER_COLOR,
  '--embed-border-radius': theme.BORDER_RADIUS,
  '--embed-shadow': theme.SHADOW,
  '--embed-font-family': theme.FONT_FAMILY
});

ChatbotWidget.propTypes = {
  config: PropTypes.object,
  onClose: PropTypes.func,
  onMinimize: PropTypes.func,
  onMaximize: PropTypes.func,
  onError: PropTypes.func,
  onMessageSent: PropTypes.func,
  onMessageReceived: PropTypes.func,
  widgetError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default ChatbotWidget;